import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";

const ManageBooking = () => {
    const [email, setEmail] = useState(localStorage.getItem("email") || "");
    const [otp, setOtp] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [otpTimer, setOtpTimer] = useState(30);
    const [verified, setVerified] = useState(localStorage.getItem("verified") === "true");
    const [userBookings, setUserBookings] = useState([]);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [userName, setUserName] = useState("");
    const [primaryPhotos, setPrimaryPhotos] = useState([]);
    const [filterStatus, setFilterStatus] = useState("booked");
    const logoutTimerRef = useRef(null);
    const auth = useAuth();

    const backendBaseUrl = "https://api.karrentals.ca";

    useEffect(() => {
        document.title = "Manage My Booking | KAR – Kelowna Auto Rentals";
        document.querySelector('meta[name="description"]')?.setAttribute("content",
            "View, modify, or cancel your KAR car rental booking. Manage your reservation online — Kelowna & Vancouver locations."
        );
    }, []);

    useEffect(() => {
        if (otpSent && otpTimer > 0) {
            const interval = setInterval(() => {
                setOtpTimer((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [otpSent, otpTimer]);

    useEffect(() => {
        if (verified) {
            startLogoutTimer();
        }
        return () => stopLogoutTimer();
    }, [verified]);

    useEffect(() => {
        const fetchPrimaryPhotos = async () => {
            try {
                const response = await axios.get(`${backendBaseUrl}/primary-photos`);
                if (response.data.success) {
                    setPrimaryPhotos(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching primary photos:", error);
            }
        };

        fetchPrimaryPhotos();
    }, []);

    useEffect(() => {
        if (verified && email) {
            fetchUserBookings();
        }
    }, [verified, email, primaryPhotos]);

    const sendOtp = async () => {
        setLoading(true);
        try {
            await axios.post(`${backendBaseUrl}/send-otp`, { email });
            setOtpSent(true);
            setOtpTimer(30);
        } catch (error) {
            console.error("Error sending OTP:", error);
        } finally {
            setLoading(false);
        }
    };

    const verifyOtp = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${backendBaseUrl}/verify-otp`, { email, otp });
            if (response.data.success) {
                setVerified(true);
                localStorage.setItem("verified", "true");
                localStorage.setItem("email", email);
                fetchUserBookings();
            }
        } catch (error) {
            console.error("Error verifying OTP:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUserBookings = async () => {
        try {
            const response = await axios.post(`${backendBaseUrl}/fetch-bookings`, { email });
            const bookings = response.data.bookings;

            if (bookings.length > 0) {
                setUserName(bookings[0].customer_name);
            }

            const bookingsWithDetails = await Promise.all(
                bookings.map(async (booking) => {
                    const [frontUrl, backUrl] = booking.license_url.split(",");
                    let carDetails = {};
                    try {
                        const carResponse = await axios.get(`${backendBaseUrl}/search-cars`, {
                            params: { car_id: booking.car_id },
                        });
                        const car = carResponse.data.data[0];
                        carDetails = {
                            carYear: car.year,
                            carName: car.name,
                            carModel: car.model,
                        };
                    } catch (error) {
                        console.error(`Error fetching car details for car ID ${booking.car_id}:`, error);
                    }

                    const primaryPhoto = primaryPhotos.find(
                        (photo) => photo.car_id === booking.car_id
                    )?.primary_photo_url;

                    return {
                        ...booking,
                        frontUrl,
                        backUrl,
                        primaryPhoto: primaryPhoto || null,
                        ...carDetails,
                    };
                })
            );

            setUserBookings(bookingsWithDetails);
        } catch (error) {
            console.error("Error fetching bookings:", error);
        }
    };

    const cancelBooking = async (bookingId, bookingDate) => {
        const isWithin24Hours = new Date() - new Date(bookingDate) <= 24 * 60 * 60 * 1000;

        const confirmationMessage = `Are you sure you want to cancel this booking?${
            isWithin24Hours
                ? "\nRefunds are available as this cancellation is within 24 hours of booking."
                : "\nRefunds are not available for cancellations made after 24 hours of booking.\nFor more information, contact us."
        }`;

        const confirmed = window.confirm(confirmationMessage);

        if (!confirmed) {
            return;
        }

        try {
            await axios.post(`${backendBaseUrl}/cancel-booking`, { bookingId });
            fetchUserBookings();
        } catch (error) {
            console.error(`Error cancelling booking with ID ${bookingId}:`, error);
        }
    };

    const startLogoutTimer = () => {
        logoutTimerRef.current = setTimeout(() => {
            handleLogout();
        }, 15 * 60 * 1000);
    };

    const stopLogoutTimer = () => {
        if (logoutTimerRef.current) {
            clearTimeout(logoutTimerRef.current);
            logoutTimerRef.current = null;
        }
    };

    const handleLogout = () => {
        localStorage.removeItem("verified");
        localStorage.removeItem("email");
        setVerified(false);
        setEmail("");
        setOtp("");
        setUserBookings([]);
    };

    const handleKarLogin = () => {
        if (auth.isAuthenticated) {
            window.location.href = "/admin-dashboard";
        } else {
            auth.signinRedirect();
        }
    };

    const filteredBookings = userBookings.filter(
        (booking) => booking.status === filterStatus
    );

    return (
        <Layout>
            <div className="container mx-auto my-6 p-6">
                < div className="relative h-[50rem] bg-cover bg-center rounded-2xl overflow-hidden hidden md:block"
                    style={{ backgroundImage: 'url("/fordbg.jpg")' }}
                >
                    <div className="bg-white p-6 rounded-lg text-black shadow-lg">
                        <div className="flex justify-between items-center">
                            <h1 className="text-2xl font-bold">Manage Bookings</h1>
                            <button
                                onClick={handleKarLogin}
                                className="flex items-center px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
                            >
                                KAR Login
                            </button>
                        </div>
                        <div className="mt-6">
                            {!verified ? (
                                <>
                                    <input
                                        type="email"
                                        placeholder="Enter your email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="block w-full mt-2 p-2 border rounded"
                                    />
                                    {otpSent && (
                                        <input
                                            type="text"
                                            placeholder="Enter OTP"
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value)}
                                            className="block w-full mt-2 p-2 border rounded"
                                        />
                                    )}
                                    <button
                                        onClick={otpSent ? verifyOtp : sendOtp}
                                        className="mt-4 px-4 py-2 bg-black text-white rounded"
                                        disabled={loading}
                                    >
                                        {otpSent ? "Verify OTP" : "Send OTP"}
                                    </button>
                                    {otpSent && otpTimer > 0 ? (
                                        <p className="mt-2 text-gray-500">
                                            Resend OTP in {otpTimer} seconds
                                        </p>
                                    ) : (
                                        otpSent && (
                                            <button
                                                onClick={sendOtp}
                                                className="mt-2 text-blue-500 underline"
                                                disabled={loading}
                                            >
                                                Resend OTP
                                            </button>
                                        )
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="flex justify-between items-center mt-4">
                                        <h2 className="text-xl font-bold">Hi, {userName}</h2>
                                        <button
                                            onClick={handleLogout}
                                            className="px-4 py-2 bg-red-500 text-white rounded"
                                        >
                                            Logout
                                        </button>
                                    </div>

                                    <div className="flex justify-center mt-4">
                                        <button
                                            onClick={() => setFilterStatus("booked")}
                                            className={`px-4 py-2 mr-2 rounded ${
                                                filterStatus === "booked"
                                                    ? "bg-black text-white"
                                                    : "bg-gray-300"
                                            }`}
                                        >
                                            Booked
                                        </button>
                                        <button
                                            onClick={() => setFilterStatus("cancelled")}
                                            className={`px-4 py-2 rounded ${
                                                filterStatus === "cancelled"
                                                    ? "bg-black text-white"
                                                    : "bg-gray-300"
                                            }`}
                                        >
                                            Cancelled
                                        </button>
                                    </div>

                                    <h3 className="mt-4 font-bold">Your Bookings:</h3>
                                    {filteredBookings.length > 0 ? (
                                        filteredBookings.map((booking) => (
                                            <div
                                                key={booking.id}
                                                className="mt-4 p-4 border rounded-xl shadow-md bg-black text-white flex flex-col sm:flex-row items-start"
                                            >
                                                {booking.primaryPhoto && (
                                                    <div className="flex-shrink-0 w-full sm:w-60 h-60 mb-4 mr-4 sm:mb-0">
                                                        <img
                                                            src={booking.primaryPhoto}
                                                            alt="Car"
                                                            className="rounded w-full h-full object-cover"
                                                        />
                                                    </div>
                                                )}
                                                <div className="flex-1">
                                                    <p>
                                                        <strong>Booking ID:</strong> {booking.id}
                                                    </p>
                                                    <p>
                                                        <strong>Booking Date:</strong> {new Date(booking.booking_date).toLocaleDateString()}
                                                    </p>
                                                    <p>
                                                        <strong>Car:</strong> {booking.carYear} {booking.carName} {booking.carModel}
                                                    </p>
                                                    <p>
                                                        <strong>Start Date:</strong> {new Date(booking.start_date).toLocaleString()}
                                                    </p>
                                                    <p>
                                                        <strong>End Date:</strong> {new Date(booking.end_date).toLocaleString()}
                                                    </p>
                                                    <p>
                                                        <strong>Total: $</strong> {booking.total_price}
                                                    </p>
                                                    {filterStatus === "booked" && (
                                                        <button
                                                            onClick={() => cancelBooking(booking.id, booking.created_at)}
                                                            className="mt-2 px-4 py-2 bg-red-500 text-white rounded w-full"
                                                        >
                                                            Cancel Booking
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="mt-4">
                                            {filterStatus === "cancelled"
                                                ? "No Cancellations found."
                                                : "No bookings found."}
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
                < div className="relative h-[36rem] bg-cover bg-center rounded-2xl overflow-hidden sm:block md:hidden"
                    style={{ backgroundImage: 'url("/fordbg1.jpg")' }}
                >
                    <div className="bg-white p-6 rounded-lg text-black shadow-lg md:hidden">
                        <div className="flex justify-between items-center">
                            <h1 className="text-xl font-bold">Manage Bookings</h1>
                            <button
                                onClick={handleKarLogin}
                                className="flex items-center px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
                            >
                                KAR Login
                            </button>
                        </div>
                        <div className="mt-6">
                            {!verified ? (
                                <>
                                    <input
                                        type="email"
                                        placeholder="Enter your email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="block w-full mt-2 p-2 border rounded"
                                    />
                                    {otpSent && (
                                        <input
                                            type="text"
                                            placeholder="Enter OTP"
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value)}
                                            className="block w-full mt-2 p-2 border rounded"
                                        />
                                    )}
                                    <button
                                        onClick={otpSent ? verifyOtp : sendOtp}
                                        className="mt-4 px-4 py-2 bg-black text-white rounded"
                                        disabled={loading}
                                    >
                                        {otpSent ? "Verify OTP" : "Send OTP"}
                                    </button>
                                    {otpSent && otpTimer > 0 ? (
                                        <p className="mt-2 text-gray-500">
                                            Resend OTP in {otpTimer} seconds
                                        </p>
                                    ) : (
                                        otpSent && (
                                            <button
                                                onClick={sendOtp}
                                                className="mt-2 text-blue-500 underline"
                                                disabled={loading}
                                            >
                                                Resend OTP
                                            </button>
                                        )
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="flex justify-between items-center mt-4">
                                        <h2 className="text-xl font-bold">Hi, {userName}</h2>
                                        <button
                                            onClick={handleLogout}
                                            className="px-4 py-2 bg-red-500 text-white rounded"
                                        >
                                            Logout
                                        </button>
                                    </div>

                                    <div className="flex justify-center mt-4">
                                        <button
                                            onClick={() => setFilterStatus("booked")}
                                            className={`px-4 py-2 mr-2 rounded ${
                                                filterStatus === "booked"
                                                    ? "bg-black text-white"
                                                    : "bg-gray-300"
                                            }`}
                                        >
                                            Booked
                                        </button>
                                        <button
                                            onClick={() => setFilterStatus("cancelled")}
                                            className={`px-4 py-2 rounded ${
                                                filterStatus === "cancelled"
                                                    ? "bg-black text-white"
                                                    : "bg-gray-300"
                                            }`}
                                        >
                                            Cancelled
                                        </button>
                                    </div>

                                    <h3 className="mt-4 font-bold">Your Bookings:</h3>
                                    {filteredBookings.length > 0 ? (
                                        filteredBookings.map((booking) => (
                                            <div
                                                key={booking.id}
                                                className="mt-4 p-4 border rounded-xl shadow-md bg-black text-white flex flex-col sm:flex-row items-start"
                                            >
                                                {booking.primaryPhoto && (
                                                    <div className="flex-shrink-0 w-full sm:w-60 h-60 mb-4 mr-4 sm:mb-0">
                                                        <img
                                                            src={booking.primaryPhoto}
                                                            alt="Car"
                                                            className="rounded w-full h-full object-cover"
                                                        />
                                                    </div>
                                                )}
                                                <div className="flex-1">
                                                    <p>
                                                        <strong>Booking ID:</strong> {booking.id}
                                                    </p>
                                                    <p>
                                                        <strong>Booking Date:</strong> {new Date(booking.booking_date).toLocaleDateString()}
                                                    </p>
                                                    <p>
                                                        <strong>Car:</strong> {booking.carYear} {booking.carName} {booking.carModel}
                                                    </p>
                                                    <p>
                                                        <strong>Start Date:</strong> {new Date(booking.start_date).toLocaleString()}
                                                    </p>
                                                    <p>
                                                        <strong>End Date:</strong> {new Date(booking.end_date).toLocaleString()}
                                                    </p>
                                                    <p>
                                                        <strong>Total: $</strong> {booking.total_price}
                                                    </p>
                                                    {filterStatus === "booked" && (
                                                        <button
                                                            onClick={() => cancelBooking(booking.id, booking.created_at)}
                                                            className="mt-2 px-4 py-2 bg-red-500 text-white rounded w-full"
                                                        >
                                                            Cancel Booking
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="mt-4">
                                            {filterStatus === "cancelled"
                                                ? "No Cancellations found."
                                                : "No bookings found."}
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default ManageBooking;
